import React from "react";
import { useQuery } from "@tanstack/react-query";
import BaseModal from "./base/BaseModal";
import TermsAppbar from "../appbar/TermsAppbar";
import SelectBox from "../select";
import Button from "../button";
import {
  useJoinTerm,
  useJoinTermNoticeDates,
} from "../../../@apis/hooks/useAuth";
import { useTermsAgreeStore } from "../../../lib/store/useTermsAgreeStore";
import { useMarketingTermsAgreeStore } from "../../../lib/store/useMarketingTermsAgreeStore";
import { useModalStore } from "../../../lib/store/useModalStore";
import type { OnErrorResponse } from "../../../@apis/dto/response/common.response";

interface TermsModalProps extends ModalComponentProps {
  html: string;
  type?: string;
  agreeTermsId?: number; // 있으면 하단 동의 버튼 노출 (직렬화 가능)
  agreeChannel?: "signup" | "marketing"; // 동의 신호를 전달할 대상 (기본: 회원가입)
}

const TermsModal = (props: TermsModalProps) => {
  const { close, html, type, agreeTermsId, agreeChannel = "signup" } = props;
  const requestAgree = useTermsAgreeStore((s) => s.requestAgree);
  const requestMarketingAgree = useMarketingTermsAgreeStore(
    (s) => s.requestAgree,
  );
  const openModal = useModalStore((s) => s.openModal);

  const { data: noticeDatesData } = useQuery({
    ...useJoinTermNoticeDates(type ?? ""),
  });

  // "20260101" → "2026.01.01"
  const formatNoticeDate = (date: string) =>
    /^\d{8}$/.test(date)
      ? `${date.slice(0, 4)}.${date.slice(4, 6)}.${date.slice(6, 8)}`
      : date;

  // API가 최신순으로 내려주므로 순서 그대로 사용 (첫 항목 = 가장 최근)
  const options = React.useMemo(
    () =>
      (noticeDatesData?.data ?? []).map((date) => ({
        label: formatNoticeDate(date),
        value: date,
      })),
    [noticeDatesData],
  );

  const [selected, setSelected] = React.useState("");

  React.useEffect(() => {
    if (options.length > 0 && !options.some((o) => o.value === selected)) {
      setSelected(options[0].value);
    }
  }, [options, selected]);

  // 선택한 시행일자별 약관 본문 조회 (없으면 전달받은 html 사용)
  const { data: termData, error: termError } = useQuery({
    ...useJoinTerm(type ?? "", selected),
  });
  const termHtml = termData?.data?.term ?? html;

  // 약관 없음(P005) → 얼럿 후 확인 시 모달 닫기
  const noticedRef = React.useRef(false);
  React.useEffect(() => {
    const bodyCode = termData?.error?.code;
    const thrownCode = (termError as unknown as OnErrorResponse)?.response?.data
      ?.error?.code;
    if (bodyCode === "P005" || thrownCode === "P005") {
      if (noticedRef.current) return;
      noticedRef.current = true;
      openModal("ALERT_DIALOG", {
        title: "알림",
        content: "약관이 없습니다.",
        onConfirm: () => close(),
      });
    }
  }, [termData, termError, openModal, close]);

  return (
    <BaseModal close={close} size="full">
      <TermsAppbar title="약관" onBack={close} />
      <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-[16px]">
        {type && options.length > 0 && (
          <div className="space-y-[8px]">
            <h1>시행일자</h1>
            <SelectBox
              options={options}
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
            />
          </div>
        )}
        <div
          className="break-words [&_*]:max-w-full [&_img]:h-auto [&_table]:w-full [&_table]:table-fixed"
          dangerouslySetInnerHTML={{ __html: termHtml }}
        />
      </div>

      {/* 하단 고정 동의 버튼 (flex 흐름상 최하단) */}
      {agreeTermsId != null && (
        <div className="shrink-0 p-[16px]">
          <Button
            className="w-full text-sm rounded-full"
            variant="black"
            label="동의"
            onClick={() => {
              if (agreeChannel === "marketing") {
                requestMarketingAgree(agreeTermsId);
              } else {
                requestAgree(agreeTermsId);
              }
              close();
            }}
          />
        </div>
      )}
    </BaseModal>
  );
};

export default React.memo(TermsModal);
